import { useState, useEffect } from 'react';

import toast from 'react-hot-toast';
import { Loader2, UserPlus, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';

import { MemberRow } from './member-row';
import { PendingInvitations } from './pending-invitations';
import { InviteMemberDialog } from './invite-member-dialog';
import { workspaceMembersApi } from '@/lib/api/workspace-members.api';
import type { MembersListResponse, WorkspaceRole } from '@optidev/shared-types';

/**
 * Members List Component
 * Main component for displaying workspace members and pending invitations
 */
export function MembersList() {
  const [data, setData] = useState<MembersListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const { isMobile } = useResponsive();

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const result = await workspaceMembersApi.getMembers();
      setData(result);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const canInvite =
    data?.currentUserRole === 'owner' || data?.currentUserRole === 'admin';

  const handleRoleChange = async (memberId: string, newRole: WorkspaceRole) => {
    try {
      await workspaceMembersApi.updateMemberRole(memberId, newRole);
      toast.success('Role updated');
      fetchMembers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      await workspaceMembersApi.removeMember(memberId);
      toast.success('Member removed');
      fetchMembers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleLeave = async () => {
    try {
      await workspaceMembersApi.leaveWorkspace();
      toast.success('You have left the workspace');
      // Redirect to dashboard - will auto-switch to another workspace
      window.location.href = '/dashboard';
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to leave workspace');
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await workspaceMembersApi.revokeInvitation(invitationId);
      toast.success('Invitation revoked');
      fetchMembers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to revoke invitation');
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await workspaceMembersApi.resendInvitation(invitationId);
      toast.success('Invitation resent');
      fetchMembers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to resend invitation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || !data.members) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Failed to load members
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col gap-4 pb-5 border-b border-border/50">
        <div className={cn(
          "flex gap-4",
          isMobile ? "flex-col" : "items-center justify-between"
        )}>
          <div>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Workspace Members</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {data.totalCount} member{data.totalCount !== 1 ? 's' : ''}
              {data.memberLimit && ` of ${data.memberLimit} allowed`}
            </p>
          </div>
          {canInvite && (
            <Button
              variant="ghost"
              onClick={() => setInviteDialogOpen(true)}
              className={cn("text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground", isMobile && "w-full")}
            >
              Invite Member
            </Button>
          )}
        </div>
      </div>

      {/* Members List */}
      <div className="divide-y divide-border/40">
        {data.members.map((member) => (
          <MemberRow
            key={member.id}
            member={member}
            currentUserRole={data.currentUserRole}
            isCurrentUserOwner={data.isCurrentUserOwner}
            onRoleChange={handleRoleChange}
            onRemove={handleRemoveMember}
            onLeave={handleLeave}
          />
        ))}
      </div>

      {/* Pending Invitations */}
      {canInvite && data.pendingInvitations.length > 0 && (
        <PendingInvitations
          invitations={data.pendingInvitations}
          onRevoke={handleRevokeInvitation}
          onResend={handleResendInvitation}
        />
      )}

      {/* Invite Dialog */}
      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onSuccess={fetchMembers}
        memberLimit={data.memberLimit}
        currentCount={data.totalCount}
      />
    </div>
  );
}
